import React, { useCallback, useContext, useEffect, useState } from 'react';

import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import { ClassifiedDiff } from 'src/components/diff-set/classified-diff';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { getIsStickyHeaderActive } from 'src/redux/pull-request/selectors';
import { useMatch } from 'src/router/hooks';
import { PR_PATH } from 'src/sections/repository/sections/pull-request/components/constants';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';
import * as styles from 'src/sections/repository/sections/pull-request/components/diff-set.styled';
import { STICKY_FILE_HEADER_HEIGHT_OFFSET } from 'src/sections/repository/sections/pull-request/components/utils/calculate-header-offset';
import { useHandleDiffObserverOnChange } from 'src/sections/repository/sections/pull-request/hooks/diff-observer-change';
import { Diff } from 'src/types/pull-request';
import { extractFilepath } from 'src/utils/extract-file-path';
import { getFileHref } from 'src/utils/get-file-href';

import { DiffPlaceholder } from './diff-placeholder';
import { useAnchorTopOffset } from './hooks/diff-set-details';

type PlaceholderDiffListProps = {
  files: Diff[];
  diffMetadataProps: DiffMetadataProps;
};

export const PlaceholderDiffList = ({
  files,
  diffMetadataProps,
}: PlaceholderDiffListProps) => {
  const { hasCommentNav } = useContext(DiffFeaturesContext);
  const [screenHeight, setScreenHeight] = useState(() => window.innerHeight);
  const anchorTopOffset = useAnchorTopOffset();
  const handleObserverOnChange = useHandleDiffObserverOnChange();
  const { path } = useMatch();
  const isStickyHeaderActive = useSelector(getIsStickyHeaderActive);
  const isPrPage = path === PR_PATH;

  // 150 gives enough buffer to highlight the first file when scrolling up into the header
  const headerScrollBuffer = 150;

  const highlightFirstFileInTree = useCallback(() => {
    if (files.length > 0 && window.scrollY < headerScrollBuffer) {
      const filepath = extractFilepath(files[0]);
      const fileHref = getFileHref(filepath);
      handleObserverOnChange(true, fileHref);
    }
  }, [files, handleObserverOnChange]);

  useEffect(() => {
    const heightObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const { height } = entry.contentRect;
        // Coerce the height to a number and check to see if the number is
        // valid before setting the height state. If height is NaN, it causes
        // an error about an invalid rootMargin for the intersection observer.
        if (!isNaN(+height)) {
          setScreenHeight(height);
        }
      });
    });

    heightObserver.observe(document.body);

    return () => {
      heightObserver.disconnect();
    };
  }, [setScreenHeight]);

  useEffect(() => {
    if (isPrPage) {
      // Highlight the first file when the files are loaded
      highlightFirstFileInTree();

      // Highlight the first file when scrolling up into the header
      window.addEventListener('scroll', highlightFirstFileInTree);

      return () =>
        window.removeEventListener('scroll', highlightFirstFileInTree);
    }
    return undefined;
  }, [highlightFirstFileInTree, isPrPage]);

  // The goal here is to create a one(ish) pixel high region -- just below the
  // bottom of the header -- in which we can detect intersection events. By
  // detecting if any of the sticky header elements intersect this small
  // region (using a threshold of 0 in InView), we can be confident that only
  // one header is intersecting it at a time, so the event we receive will be
  // the relevant one.
  //
  // If there's no sticky header, we fall back to a hard-coded offset that gets
  // us out of the global page header. This works for the pages (commit,
  // branch) that don't have a sticky header. It also works for the pull
  // request page because (as of this moment) the resulting value places the
  // intersection observer higher than the diff, so it will never be
  // intersected. Since we highlight the first value by default and scrolling
  // up should cause the first file to pass through the observer, this should
  // work well enough.
  const headerElem = document.getElementById('sticky-header-content');
  const headerOffset =
    isStickyHeaderActive && headerElem
      ? headerElem.offsetHeight + headerElem.offsetTop
      : STICKY_FILE_HEADER_HEIGHT_OFFSET;

  // The will be negative to inset the margin from the edge of the document. We
  // make them negative here rather than add the - sign in the string
  // interpolation to avoid two - signs, which causes an error.
  const rootMarginTop = -(headerOffset + 20);
  const rootMarginBottom = -(screenHeight + rootMarginTop - 1);

  return (
    <>
      {files.map((file, index) => {
        const filepath = extractFilepath(file);
        const fileHref = getFileHref(filepath);

        return (
          <styles.DiffSetFileWrapper
            isSingleFileModeActive={false}
            hasCommentNav={hasCommentNav}
            key={index}
          >
            <styles.ScrollToAnchor
              topOffset={anchorTopOffset}
              id={fileHref}
              hasCommentNav={hasCommentNav}
            />
            <InView
              rootMargin={`${rootMarginTop}px 0px ${rootMarginBottom}px 0px`}
              threshold={0}
              onChange={inView => handleObserverOnChange(inView, fileHref)}
            >
              <InView triggerOnce>
                {({ inView, ref }) =>
                  inView ? (
                    <ClassifiedDiff
                      file={file}
                      index={index}
                      diffMetadataProps={diffMetadataProps}
                    />
                  ) : (
                    <div ref={ref}>
                      <DiffPlaceholder file={file} />
                    </div>
                  )
                }
              </InView>
            </InView>
          </styles.DiffSetFileWrapper>
        );
      })}
    </>
  );
};
